/**
 * PaymentMix — how today's money arrived.
 *
 * A horizontal stacked bar, not the donut it was drawn as. Part-to-whole is a
 * stacked bar's job: angles are harder to compare than lengths, a donut cannot
 * be direct-labelled without leader lines, and it needs far more height to say
 * the same thing. The bar also degrades honestly — at one method it is simply a
 * full bar, where a donut becomes a ring with no information in it.
 *
 * Segments carry their own labels and a 2px surface gap, which is what lets the
 * palette stand up: in dark mode the gold/green pair sits in the CVD floor band
 * and is legal only with secondary encoding.
 */
import { Card } from '@/components/ui/Card';
import { Money } from '@/components/ui/numeric';
import { PAYMENT_COLORS, CAT_OTHER } from './chartTokens';

export interface MethodSlice {
  method: string;
  label: string;
  amount: number;
}

export function PaymentMix({ slices }: { slices: MethodSlice[] }) {
  const total = slices.reduce((a, s) => a + s.amount, 0);

  return (
    <Card className="p-5">
      <h2 className="text-heading font-medium text-fg-primary">Payment methods</h2>
      <p className="mb-4 text-label text-fg-tertiary">
        {total > 0 ? 'Share of money collected today' : 'Nothing collected today yet'}
      </p>

      {total <= 0 ? (
        <div className="h-3 w-full rounded-full bg-bg-subtle" aria-hidden />
      ) : (
        <>
          <div
            className="flex h-3 w-full gap-[2px] overflow-hidden rounded-full"
            role="img"
            aria-label={slices
              .map((s) => `${s.label} ${Math.round((s.amount / total) * 100)}%`)
              .join(', ')}
          >
            {slices.map((s) => (
              <div
                key={s.method}
                className="h-full first:rounded-l-full last:rounded-r-full"
                style={{
                  width: `${(s.amount / total) * 100}%`,
                  backgroundColor: PAYMENT_COLORS[s.method] ?? CAT_OTHER,
                }}
              />
            ))}
          </div>

          {/* The legend is also the table: every series named, with its value in
              text tokens rather than in its own hue. */}
          <ul className="mt-4 space-y-2">
            {slices.map((s) => (
              <li key={s.method} className="flex items-center gap-2.5 text-body">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: PAYMENT_COLORS[s.method] ?? CAT_OTHER }}
                  aria-hidden
                />
                <span className="text-fg-secondary">{s.label}</span>
                <span className="ml-auto tabular-nums text-fg-tertiary">
                  {Math.round((s.amount / total) * 100)}%
                </span>
                <span className="w-24 text-right text-fg-primary">
                  <Money value={s.amount} />
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
